import React, { useEffect, useState } from "react";
import { getUserConsentHistory } from "../services/adminService";
import { formatUserDate, dateMillis } from "../utils/user-directory";
import { specialtyName } from "../data/specialties";
export default function UserConsentHistory({ uid }: { uid: string }) {
  const [records, setRecords] = useState<any[]>([]),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [search, setSearch] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getUserConsentHistory(uid)
      .then((result) => {
        if (active)
          setRecords(
            result.records.sort(
              (a, b) =>
                (dateMillis(b.acceptedAt || b.createdAt) || 0) -
                (dateMillis(a.acceptedAt || a.createdAt) || 0),
            ),
          );
      })
      .catch((e: Error) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [uid]);
  return (
    <details className="consent-history">
      <summary>Purchase and consent history</summary>
      <p>
        Showing up to 500 records from each source for this user. Older records without a wording version or
        acceptance timestamp are incomplete evidence; no consent is inferred.
      </p>
      <label>
        Find an order or event
        <input
          className="form-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>
      {loading ? (
        <p role="status">Loading history…</p>
      ) : error ? (
        <p role="alert">{error}</p>
      ) : records.length === 0 ? (
        <p>No consent or purchase records found.</p>
      ) : (
        records
          .filter((row) =>
            JSON.stringify(row).toLowerCase().includes(search.toLowerCase()),
          )
          .map((row) => (
            <details
              key={`${row.type}-${row.id}`}
              style={{ border: "1px solid #666", padding: 12, marginTop: 10 }}
            >
              <summary>
                {row.type} ·{" "}
                {row.plan || row.status || row.consent?.version || ""} ·{" "}
                {formatUserDate(row.acceptedAt || row.createdAt)} · {row.id}
              </summary>
              <dl>
                <dt>Evidence</dt>
                <dd>
                  {row.evidenceStatus ||
                    (row.version ? "Recorded" : "Legacy / incomplete")}
                </dd>
                <dt>Acceptance received (UK time)</dt>
                <dd>
                  {formatUserDate(row.acceptedAt || row.consent?.acceptedAt)}
                </dd>
                <dt>Terms accepted</dt>
                <dd>
                  {typeof (row.consent?.terms ?? row.terms) === "boolean"
                    ? (row.consent?.terms ?? row.terms)
                      ? "Yes"
                      : "No"
                    : "Not recorded"}
                </dd>
                <dt>Immediate-supply acknowledgement</dt>
                <dd>
                  {typeof row.consent?.waiver === "boolean"
                    ? row.consent.waiver
                      ? "Yes"
                      : "No"
                    : "Not recorded"}
                </dd>
                <dt>Marketing choice</dt>
                <dd>
                  {typeof (row.consent?.marketing ?? row.marketing) ===
                  "boolean"
                    ? (row.consent?.marketing ?? row.marketing)
                      ? "Opted in"
                      : "Not opted in"
                    : row.status || "Not recorded"}
                </dd>
                <dt>Document version</dt>
                <dd>{row.consent?.version || row.version || "Not recorded"}</dd>
                <dt>Source</dt>
                <dd>{row.consent?.source || row.source || "Not recorded"}</dd>
                <dt>Specialty</dt>
                <dd>
                  {row.specialtyId ? specialtyName(row.specialtyId) : "—"}
                </dd>
                <dt>Payment fulfilment</dt>
                <dd>{formatUserDate(row.fulfilledAt)}</dd>
                <dt>Downloadable confirmation available</dt>
                <dd>{formatUserDate(row.confirmationAvailableAt)}</dd>
                <dt>Payment receipt emails</dt>
                <dd>Managed in Stripe; delivery is not verified here.</dd>
              </dl>
              {(row.consent?.statements || row.statements || row.statement) && (
                <pre
                  style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                >
                  {JSON.stringify(
                    row.consent?.statements || row.statements || row.statement,
                    null,
                    2,
                  )}
                </pre>
              )}
              {row.confirmationText && (
                <details>
                  <summary>Accepted Terms and purchase confirmation</summary>
                  <pre
                    style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                  >
                    {row.confirmationText}
                  </pre>
                </details>
              )}
            </details>
          ))
      )}
    </details>
  );
}
